import React, { useState, useEffect } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { MapPin, ArrowLeft, Loader2, Save, Users, ExternalLink, Info } from 'lucide-react'
import { useMutation, useQuery } from 'react-query'
import { areasService } from '@/services/areas'
import { brandsService } from '@/services/brands'
import { usersService } from '@/services/users'
import { NavigationUtils } from '@/utils/navigation'
import { useAuth } from '@/context/AuthContext'
import toast from 'react-hot-toast'

const AreaEdit = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const { hasRole } = useAuth()

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    brand_id: '',
    is_active: true
  })

  const [errors, setErrors] = useState({})

  // Check permissions using useEffect
  useEffect(() => {
    if (!hasRole('admin') && !hasRole('manager')) {
      NavigationUtils.navigateBackToSettings(navigate, 'areas')
    }
  }, [navigate, hasRole])

  // Fetch area details
  const {
    data: area,
    isLoading: areaLoading,
    error: areaError
  } = useQuery(
    ['area', id],
    () => areasService.getById(id),
    {
      onError: (error) => {
        if (error.response?.status === 404) {
          toast.error('Area not found')
          NavigationUtils.navigateBackToSettings(navigate, 'areas')
        } else {
          toast.error('Failed to load area details')
        }
      }
    }
  )

  // Populate form when area data is available (from cache or server)
  useEffect(() => {
    if (area) {
      setFormData({
        name: area.name || '',
        description: area.description || '',
        brand_id: area.brand_id?.toString() || '',
        is_active: area.is_active ?? true
      })
    }
  }, [area])

  // Fetch brands for dropdown
  const { data: brandsData, isLoading: brandsLoading } = useQuery(
    ['brands'],
    () => brandsService.getAll({ page_size: 1000 }),
    {
      onError: (error) => {
        toast.error('Failed to load brands')
        console.error('Error loading brands:', error)
      }
    }
  )

  // Note: Area manager assignment is now handled through user management
  // Users with area manager roles can be assigned to areas via the user creation/editing forms

  // Update mutation
  const updateMutation = useMutation(
    (areaData) => areasService.update(id, areaData),
    {
      onSuccess: (data) => {
        toast.success('Area updated successfully')
        navigate(`/areas/${data.id}`) // Navigate to area detail page
      },
      onError: (error) => {
        const errorMessage = error.response?.data?.detail || 'Failed to update area'
        toast.error(errorMessage)
        
        // Handle validation errors
        if (error.response?.data?.validation_errors) {
          setErrors(error.response.data.validation_errors)
        }
      }
    }
  )

  // Handle form changes
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }))
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: null
      }))
    }
  }

  // Validate form
  const validateForm = () => {
    const newErrors = {}

    if (!formData.name.trim()) {
      newErrors.name = 'Area name is required'
    }

    if (!formData.brand_id) {
      newErrors.brand_id = 'Brand is required'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  // Handle form submission
  const handleSubmit = (e) => {
    e.preventDefault()
    
    if (!validateForm()) {
      return
    }

    const submitData = {
      name: formData.name,
      description: formData.description || null,
      is_active: formData.is_active
    }

    updateMutation.mutate(submitData)
  }

  if (areaLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="flex items-center">
          <Loader2 className="h-8 w-8 animate-spin text-blue-600 mr-3" />
          <span className="text-gray-600">Loading area details...</span>
        </div>
      </div>
    )
  }

  if (areaError) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Error Loading Area</h2>
          <p className="text-gray-600 mb-4">There was a problem loading the area details.</p>
          <button
            onClick={() => NavigationUtils.navigateBackToSettings(navigate, 'areas')}
            className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700"
          >
            Back to Areas
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate(`/areas/${id}`)}
            className="btn-outline btn-sm flex items-center"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Area
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Edit Area</h1>
            <p className="mt-1 text-sm text-gray-600">
              Update area information and configuration
            </p>
          </div>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="bg-white shadow-sm rounded-lg border border-gray-200">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900">Area Information</h3>
            <p className="mt-1 text-sm text-gray-600">
              Update area details and configuration
            </p>
          </div>
          <div className="p-6 space-y-6">
            {/* Area Name */}
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                Area Name *
              </label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                className={`w-full border rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.name ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="Enter area name"
              />
              {errors.name && (
                <p className="mt-1 text-sm text-red-600">{errors.name}</p>
              )}
            </div>

            {/* Description */}
            <div>
              <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-2">
                Description
              </label>
              <textarea
                id="description"
                name="description"
                value={formData.description}
                onChange={handleChange}
                rows={3}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Enter area description (optional)"
              />
            </div>

            {/* Brand Selection - Read Only */}
            <div>
              <label htmlFor="brand_id" className="block text-sm font-medium text-gray-700 mb-2">
                Brand
              </label>
              <input
                type="text"
                value={area?.brand_name || 'N/A'}
                readOnly
                className="w-full border border-gray-300 rounded-md px-3 py-2 bg-gray-50 text-gray-600"
              />
              <p className="mt-1 text-sm text-gray-500">
                Brand cannot be changed after area creation
              </p>
            </div>

            {/* Area Manager Assignment Note */}
            <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
              <div className="flex">
                <div className="flex-shrink-0">
                  <Info className="h-5 w-5 text-blue-600" />
                </div>
                <div className="ml-3 flex-1">
                  <h3 className="text-sm font-medium text-blue-800 mb-2">
                    User Assignment Management
                  </h3>
                  <div className="text-sm text-blue-700 space-y-2">
                    <p>
                      Managers and users are assigned to this area through the{' '}
                      <strong>User Management</strong> section. This centralized approach
                      ensures consistent permissions and easy management across all areas.
                    </p>

                    <div className="mt-3 p-3 bg-white rounded border border-blue-200">
                      <h4 className="font-medium text-blue-900 mb-2">Current Assignment Rules:</h4>
                      <ul className="space-y-1 text-sm">
                        <li>• <strong>Area Manager:</strong> Only one per area </li>
                        <li>• <strong>Regular Users:</strong> Multiple users allowed </li>
                        <li>• <strong>Conflict Prevention:</strong> System prevents multiple area managers per area</li>
                        <li>• <strong>Role-Based:</strong> Assignment depends on user's role (Area Manager vs User)</li>
                      </ul>
                    </div>

                    <div className="mt-3">
                      <Link
                        to="/settings?tab=users"
                        className="inline-flex items-center text-sm font-medium text-blue-600 hover:text-blue-800"
                      >
                        Manage User Assignments
                        <ExternalLink className="h-3 w-3 ml-1" />
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Status */}
            <div>
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="is_active"
                  name="is_active"
                  checked={formData.is_active}
                  onChange={handleChange}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <label htmlFor="is_active" className="ml-2 block text-sm text-gray-700">
                  Active
                </label>
              </div>
              <p className="mt-1 text-sm text-gray-500">
                Inactive areas will not be available for user assignment
              </p>
            </div>

          </div>
        </div>

        {/* Submit Buttons */}
        <div className="flex justify-end space-x-4">
          <button
            type="button"
            onClick={() => navigate(`/areas/${id}`)}
            className="btn-outline btn-md"
            disabled={updateMutation.isLoading}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="btn-primary btn-md flex items-center"
            disabled={updateMutation.isLoading}
          >
            {updateMutation.isLoading ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                Updating...
              </>
            ) : (
              <>
                <Save className="h-4 w-4 mr-2" />
                Update Area
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  )
}

export default AreaEdit
